import { createSelector } from 'reselect'
import { convertData2JsonForm } from '../../../../shared/lib/convertData2JsonForm'
import { objectMap } from '../../../../../lib/utils/get-table-transform-schema-and-form-data'
import { addEnumValBySchema } from '../../../../shared/lib/addEnumValBySchema'
import { hasPermission, hasPermissionScope } from '../../../../lib/permissions'
import { Permissions } from '../../../../constants/permissions'
import {
  selectTimelineFiles as selectAllTimelineFiles,
  selectTimelineData as selectAllTimelineData,
} from '../../../../components/v2/activity-timeline/selectors'

export const createSession = createSelector(
  (state: any) => state.session,
  sessionData => sessionData,
)
const selectMeasurement = state => state.measurement

export const getMeasurementId = createSelector(selectMeasurement, ({ id }) => id)

const selectMeasurementForm = createSelector(selectMeasurement, ({ form }) => form)

const selectMeasurementEnums = createSelector(selectMeasurement, ({ enums }) => enums)

export const getMeasurementFormData = createSelector(selectMeasurementForm, ({ data }) => data)

export const getMeasurementFormError = createSelector(selectMeasurementForm, ({ error }) => error)

export const getMeasurementEnumsData = createSelector(selectMeasurementEnums, ({ data }) => data)

export const getMeasurementEnumsError = createSelector(selectMeasurementEnums, ({ error }) => error)

export const getMeasurementParentId = createSelector(
  selectMeasurementForm,
  ({ data }) => data?.parent_id,
)

export const getMeasurementFormLoading = createSelector(
  selectMeasurementForm,
  ({ isLoading }) => isLoading,
)

export const getMeasurementStageCurrent = createSelector(
  getMeasurementFormData,
  formData => formData?.stages?.current,
)

export const getMeasurementEnumsIsLoading = createSelector(
  selectMeasurementEnums,
  ({ isLoading }) => isLoading,
)

const selectChatAvito = state => state.chatAvito

export const selectChatAvitoIsLoading = createSelector(
  selectChatAvito,
  ({ isLoading }) => isLoading,
)

export const selectChatAvitoData = createSelector(selectChatAvito, ({ data }) => data)

export const selectChatAvitoError = createSelector(selectChatAvito, ({ error }) => error)

export const getActiveAvitoChatId = createSelector(
  selectChatAvito,
  ({ activeChatId }) => activeChatId,
)

export const getActiveAvitoChat = createSelector(
  [selectChatAvitoData, getActiveAvitoChatId],
  (data, activeChatId) => {
    return data?.filter(chatItem => chatItem.chat_id === activeChatId)[0]
  },
)

export const getVisibleFormData = createSelector(
  [getMeasurementFormData, getMeasurementEnumsData],
  (formData, enums) => {
    if (formData && enums) {
      const { data: form, customer_id } = formData

      if (!form) {
        return false
      }

      const { resultObject, resultSchema } = convertData2JsonForm(form)

      const enumItems = enums?.['enum'] || []
      if (enums?.['city']) {
        enumItems.city_id = Object.values(
          objectMap(enums['city'], ([key, value]) => {
            return [key, { id: parseInt(key), value: value }]
          }),
        )
      }

      if (enums?.['region']) {
        enumItems.region_id = Object.values(
          objectMap(enums['region'], ([key, value]) => {
            return [key, { id: parseInt(key), value: value }]
          }),
        )
      }

      Object.keys(enumItems).forEach(code => {
        if (resultSchema?.properties.hasOwnProperty(code)) {
          const enumValues = enums?.['enum']?.[code] || {}
          const oneOf: Record<string, string>[] = []

          Object.keys(enumValues).forEach(enumValue => {
            return oneOf.push({
              const: enumValues[enumValue].id,
              title: enumValues[enumValue].value,
            })
          })

          if (resultSchema?.properties.hasOwnProperty(code)) {
            addEnumValBySchema(resultSchema, code, oneOf, enumValues)
          }

          if (resultObject.hasOwnProperty(code)) {
            resultObject[code] = resultObject[code] ? parseInt(resultObject[code]) : null
          }
        }
      })
      if (resultSchema?.properties.hasOwnProperty('phone')) {
        resultSchema.properties['phone'] = {
          ...resultSchema.properties['phone'],
          options: {
            ...resultSchema.properties['phone'].options,
            customer_id,
          },
        }
      }

      return { resultSchema, resultObject }
    }
  },
)

// Селектор для получения ID всех файлов из полей формы замера
const getMeasurementFileIds = createSelector(getMeasurementFormData, formData => {
  if (!formData?.data) return []
  const fileIds: number[] = []

  const processField = field => {
    if (Array.isArray(field.value) && field.value.length > 0) {
      field.value.forEach(file => {
        if (file?.id && typeof file.id === 'number') {
          fileIds.push(file.id)
        }
      })
    }
  }

  formData.data.forEach(item => {
    if (item.fields) {
      item.fields.forEach(processField)
    } else {
      processField(item)
    }
  })
  return fileIds
})

export const selectFilteredTimelineFiles = createSelector(
  [selectAllTimelineFiles, getMeasurementFileIds, createSession],
  (timelineFiles, measurementFileIds, session) => {
    const pws = session?.sessionData?.permissionsWithScope
    const isMeasurementManager = hasPermission(pws, Permissions.ACCESS_MEASUREMENT)
    const isSalesManager = hasPermissionScope(pws, Permissions.ACCESS_ORDERS, ['office'])

    if (!isMeasurementManager || isSalesManager) {
      return timelineFiles
    }

    if (!measurementFileIds || measurementFileIds.length === 0) {
      return []
    }

    return timelineFiles
      ?.map(activityItems => {
        const filteredFiles = activityItems?.items?.filter(item => {
          return item.type === 'file' && item.data?.id && measurementFileIds.includes(item.data.id)
        })
        return filteredFiles?.length ? { ...activityItems, items: filteredFiles } : null
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
  },
)

export const selectFilteredTimelineData = createSelector(
  [selectAllTimelineData, getMeasurementFileIds, createSession],
  (timelineData, measurementFileIds, session) => {
    const pws = session?.sessionData?.permissionsWithScope
    const isMeasurementManager = hasPermission(pws, Permissions.ACCESS_MEASUREMENT)
    const isSalesManager = hasPermissionScope(pws, Permissions.ACCESS_ORDERS, ['office'])

    if (!isMeasurementManager || isSalesManager) {
      return timelineData
    }

    if (!timelineData?.items || !measurementFileIds || measurementFileIds.length === 0) {
      return {
        ...timelineData,
        items: timelineData?.items
          ?.map(activityItems => ({
            ...activityItems,
            items: activityItems?.items?.filter(item => item.type !== 'file'),
          }))
          .filter(activityItems => activityItems?.items?.length > 0),
      }
    }

    return {
      ...timelineData,
      items: timelineData?.items
        ?.map(activityItems => ({
          ...activityItems,
          items: activityItems?.items?.filter(item => {
            if (item.type === 'file') {
              return item.data?.id && measurementFileIds.includes(item.data.id)
            }
            return true
          }),
        }))
        .filter(activityItems => activityItems?.items?.length > 0),
    }
  },
)
